import { useEffect } from 'react';
import { useNavigate, useMatch, Outlet } from 'react-router-dom';
import { PermissionDenied, PermissionEnum, IconLoader } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetReportsCurrenciesQuery,
  useLazyGetReportsBaseCurrencyQuery,
  useLazyGetReportsDictionariesAccountsCategoriesQuery,
} from '../../store/dictionaries/service';
import { setBSAccountsCategories } from '../balance/store/slices/sheet';
import { setPLAccountsCategories } from '../profit-loss/store/slices/sheet';
import { setSafeguardingAccountsCategories } from '../safeguarding/store/slice';
import { setCAAccountsCategories } from '../capital-adequacy/store/slice';
import { setBanksAccountsCategories } from '../bank-and-psp/store/slice';

import ROUTES from '../../routes';

import cn from './reports.module.css';

const getReportsLink = (permission: PermissionEnum) => {
  let link;

  switch (permission) {
    case PermissionEnum.REPORTS_BALANCE_SHEET_LIST:
      link = ROUTES.REPORTS_BALANCE;
      break;
    case PermissionEnum.REPORTS_PROFIT_LOSS_LIST:
      link = ROUTES.REPORTS_PROFIT_lOSS;
      break;
    case PermissionEnum.REPORTS_SAFEGUARDING_LIST:
      link = ROUTES.REPORTS_SAFEGUARDING;
      break;
    case PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST:
      link = ROUTES.REPORTS_CAPITAL_ADEQUACY;
      break;
    case PermissionEnum.REPORTS_PSP_LIST:
      link = ROUTES.REPORTS_BANK_AND_PSP;
      break;
    case PermissionEnum.REPORTS_CURRENCY_POSITION_LIST:
      link = ROUTES.REPORTS_CURRENCY_POSITION;
      break;
    default:
      break;
  }

  return link as string;
};

const Reports = () => {
  const match = useMatch(ROUTES.REPORTS);

  const { activePermissions, currencies, baseCurrency, accountsCategories } =
    useAppSelector((state) => state.reportsDictionaries);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getReportsCurrencies] = useLazyGetReportsCurrenciesQuery();
  const [getReportsBaseCurrency] = useLazyGetReportsBaseCurrencyQuery();
  const [getReportsAccountsCategories] =
    useLazyGetReportsDictionariesAccountsCategoriesQuery();

  useEffect(() => {
    if (match && activePermissions?.includes(PermissionEnum.SECTION_REPORTS)) {
      const reportsPages = [
        PermissionEnum.REPORTS_BALANCE_SHEET_LIST,
        PermissionEnum.REPORTS_PROFIT_LOSS_LIST,
        PermissionEnum.REPORTS_SAFEGUARDING_LIST,
        PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
        PermissionEnum.REPORTS_PSP_LIST,
        PermissionEnum.REPORTS_CURRENCY_POSITION_LIST,
      ];

      const activeReportsPages: PermissionEnum[] = [];

      reportsPages.forEach((item) => {
        if (activePermissions.includes(item)) {
          activeReportsPages.push(item);
        }
      });

      if (activeReportsPages.length) {
        navigate(getReportsLink(activeReportsPages[0]));
      } else {
        navigate('/');
      }
    }
  }, [match, activePermissions]);

  useEffect(() => {
    if (!currencies) {
      getReportsCurrencies();
    }

    if (!baseCurrency) {
      getReportsBaseCurrency();
    }
  }, [currencies, baseCurrency]);

  useEffect(() => {
    if (!accountsCategories) {
      getReportsAccountsCategories();
    }
  }, [accountsCategories]);

  useEffect(() => {
    if (accountsCategories) {
      dispatch(setBSAccountsCategories(accountsCategories));

      dispatch(setPLAccountsCategories(accountsCategories));

      dispatch(setSafeguardingAccountsCategories(accountsCategories));

      dispatch(setCAAccountsCategories(accountsCategories));

      dispatch(setBanksAccountsCategories(accountsCategories));
    }
  }, [accountsCategories]);

  useEffect(() => {
    let title = 'Reports';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.reportsLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (activePermissions.includes(PermissionEnum.SECTION_REPORTS)) {
      content = <Outlet />;
    } else {
      content = <PermissionDenied />;
    }
  }

  return content;
};

export default Reports;
